
import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { adminDataService, AdminStats, RecentActivity } from '@/services/adminDataService';
import { supabase } from '@/integrations/supabase/client';

export const useAdminDashboard = () => {
  const { user, hasAccess } = useSupabaseAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    if (!user || !hasAccess('admin')) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [statsData, activitiesData] = await Promise.all([
        adminDataService.getSystemStats(),
        adminDataService.getRecentActivities()
      ]);

      setStats(statsData);
      setActivities(activitiesData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [user, hasAccess]);

  // Set up real-time subscriptions for live updates
  useEffect(() => {
    if (!user || !hasAccess('admin')) return;

    const channels: any[] = [];

    // Subscribe to course assignment changes
    const assignmentChannel = supabase
      .channel('admin_course_assignments')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'course_assignments' },
        () => {
          console.log('Course assignment changed, refreshing data');
          loadDashboardData();
        }
      )
      .subscribe();

    // Subscribe to user profile changes
    const profileChannel = supabase
      .channel('admin_profiles')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles' },
        () => {
          console.log('User profile changed, refreshing data');
          loadDashboardData();
        }
      )
      .subscribe();

    // Subscribe to certificate issuance
    const certificateChannel = supabase
      .channel('admin_certificates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'certificates' },
        () => {
          console.log('New certificate issued, refreshing data');
          loadDashboardData();
        }
      )
      .subscribe();

    channels.push(assignmentChannel, profileChannel, certificateChannel);

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [user, hasAccess]);

  return {
    stats,
    activities,
    loading,
    error,
    refreshData: loadDashboardData,
    hasAccess: hasAccess('admin')
  };
};
